import { useMemo } from 'react';
import { Autocomplete, TextField, Tooltip } from '@mui/material';
import type { ApmcOption } from '@features/apmcs/schema';
import type { CropOption } from '@features/crops/schema';
import type { VarietyOption } from '@features/varieties/schema';
import { todayLocal } from '../dateRange';

export interface MarketPriceFilters {
  cropId: number | null;
  apmcId: number | null;
  varietyId: number | null;
  dateFrom: string; // YYYY-MM-DD or ''
  dateTo: string; // YYYY-MM-DD or ''
}

interface Props {
  value: MarketPriceFilters;
  onChange: (next: MarketPriceFilters) => void;
  crops: CropOption[];
  apmcs: ApmcOption[];
  varieties: VarietyOption[];
  loading?: boolean;
}

export function MarketPriceFiltersBar({
  value,
  onChange,
  crops,
  apmcs,
  varieties,
  loading,
}: Props) {
  const cropById = useMemo(() => new Map(crops.map((c) => [c.id, c])), [crops]);
  const apmcById = useMemo(() => new Map(apmcs.map((a) => [a.id, a])), [apmcs]);

  // Variety options are scoped to the picked crop. Without a crop, the
  // dropdown is disabled — picking a variety in isolation is meaningless
  // (varieties belong to a crop) and would also produce a huge flat list.
  const varietyOptions = useMemo(
    () =>
      value.cropId == null
        ? []
        : varieties.filter((v) => v.cropId === String(value.cropId)),
    [varieties, value.cropId],
  );
  const varietyById = useMemo(
    () => new Map(varietyOptions.map((v) => [v.id, v])),
    [varietyOptions],
  );

  const cropValue = value.cropId == null ? null : cropById.get(String(value.cropId)) ?? null;
  const apmcValue = value.apmcId == null ? null : apmcById.get(String(value.apmcId)) ?? null;
  const varietyValue =
    value.varietyId == null ? null : varietyById.get(String(value.varietyId)) ?? null;
  const varietyDisabled = value.cropId == null;

  // Real-world prices can't be reported for the future; the From/To range must
  // also be coherent (From ≤ To). Both bounds compute from the current `value`
  // so the constraints stay in sync as the editor types.
  const today = todayLocal();
  const fromMax = value.dateTo || today;
  const toMin = value.dateFrom || undefined;

  // Wrapper kept as a function so the disabled-only Tooltip doesn't pollute
  // the enabled control with an empty-title popper that intercepts hovers.
  const variety = (
    <Autocomplete
      size="small"
      options={varietyOptions}
      loading={loading}
      disabled={varietyDisabled}
      value={varietyValue}
      onChange={(_, opt) =>
        onChange({ ...value, varietyId: opt ? Number(opt.id) : null })
      }
      getOptionLabel={(opt) => opt.name}
      isOptionEqualToValue={(opt, v) => opt.id === v.id}
      noOptionsText="No varieties for this crop"
      className="w-full md:w-[170px]"
      // No `startAdornment` lock icon when disabled — MUI auto-shrinks the
      // label whenever a startAdornment is present, which made the disabled
      // field's label sit ABOVE the input while every other (empty) field's
      // label sat in the middle. That visual inconsistency read as "broken".
      // The disabled greyed-out state + the wrapping Tooltip's "Pick a crop
      // first" hover hint communicate the dependency adequately.
      renderInput={(p) => <TextField {...p} label="Variety" />}
    />
  );

  return (
    <>
      <Autocomplete
        size="small"
        options={crops}
        loading={loading}
        value={cropValue}
        onChange={(_, opt) =>
          onChange({
            ...value,
            cropId: opt ? Number(opt.id) : null,
            // Crop change invalidates the picked variety — reset so the user
            // doesn't end up with a stale variety from the previous crop.
            varietyId: null,
          })
        }
        getOptionLabel={(opt) => opt.name}
        isOptionEqualToValue={(opt, v) => opt.id === v.id}
        className="w-full md:w-[170px]"
        renderInput={(p) => <TextField {...p} label="Crop" />}
      />
      {varietyDisabled ? (
        <Tooltip title="Pick a crop first to enable Variety" arrow>
          {/* Tooltip needs a non-disabled child to receive hovers; wrap in span. */}
          <span className="block w-full md:inline-block md:w-auto">{variety}</span>
        </Tooltip>
      ) : (
        variety
      )}
      <Autocomplete
        size="small"
        options={apmcs}
        loading={loading}
        value={apmcValue}
        onChange={(_, opt) => onChange({ ...value, apmcId: opt ? Number(opt.id) : null })}
        getOptionLabel={(opt) => opt.name}
        isOptionEqualToValue={(opt, v) => opt.id === v.id}
        className="w-full md:w-[200px]"
        renderInput={(p) => <TextField {...p} label="Mandi (APMC)" />}
      />
      <TextField
        label="From"
        size="small"
        type="date"
        value={value.dateFrom}
        onChange={(e) => onChange({ ...value, dateFrom: e.target.value })}
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { max: fromMax },
        }}
        className="w-full md:w-[145px]"
      />
      <TextField
        label="To"
        size="small"
        type="date"
        value={value.dateTo}
        onChange={(e) => onChange({ ...value, dateTo: e.target.value })}
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { max: today, min: toMin },
        }}
        className="w-full md:w-[145px]"
      />
    </>
  );
}
